import React, { useState, useRef, useEffect, useCallback } from 'react';
import { AssetUrls } from 'config/assets';
import { SORT_OPTIONS, ChevronSvg } from './sendToolbarControls';

interface Props {
  sortValue: string;
  onSortChange?: (value: string) => void;
  isSortLoading?: boolean;
}

export default function SendSortDropdown({
  sortValue,
  onSortChange,
  isSortLoading = false,
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const currentLabel = SORT_OPTIONS.find(o => o.value === sortValue)?.label || 'Default';

  const handleSelect = useCallback((value: string) => {
    onSortChange?.(value);
    setIsOpen(false);
  }, [onSortChange]);

  return (
    <div className="send-sort-selector" ref={ref}>
      <button
        type="button"
        className={`send-sort-trigger ${isOpen ? 'open' : ''} ${isSortLoading ? 'is-loading' : ''}`}
        onClick={() => {
          if (isSortLoading) return;
          setIsOpen(o => !o);
        }}
        disabled={isSortLoading}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label="Select sort order"
        style={isSortLoading ? { opacity: 0.6, cursor: 'wait' } : undefined}
      >
        {isSortLoading ? (
          <span className="loading-spinner-small" />
        ) : (
          <img src={AssetUrls.icons.controls.sort} alt="" className="send-sort-icon" />
        )}
        <span className="send-sort-text">{currentLabel}</span>
        <ChevronSvg open={isOpen} />
      </button>
      {isOpen && !isSortLoading && (
        <div className="send-sort-menu" role="listbox">
          {SORT_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`send-sort-option ${sortValue === option.value ? 'selected' : ''}`}
              onClick={() => handleSelect(option.value)}
              role="option"
              aria-selected={sortValue === option.value}
            >
              <span>{option.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
